import * as React from 'react'
import { useRef, Fragment } from 'react'
import { markdown } from 'markdown'
import { useIntl } from 'react-intl'

// Components
import { Field as BaseField, Select, Tooltip, Icon } from '@metrisk/react_metrisk-components'
import DatePickerInput from '@components/DatePicker/DatePickerInput'
import CurrencyInput from '@components/Currency'
import BankDetails from '@components/BankDetails'
import ClaimReasonInput from '@components/ClaimReasonInput'
import PasswordInput from '@components/PasswordInput'
import RewardTrackingOptIn from '@components/RewardTrackingOptIn/RewardTrackingOptIn'
import PurchaseInformation from '@components/PurchaseInformation/PurchaseInformation'
import PurchaseItems from '../PurchaseItems/PurchaseItems'

// Components
import { RadioGroup } from '@components/RadioGroup'
import Uploader from '@components/Upload'

import './Field.scss'

const Field: React.FunctionComponent<Field.IProps> = ({
  label,
  tooltip,
  valid,
  formFields,
  fieldsToRender,
  upload,
  ...props
}: any) => {
  const intl = useIntl()
  const ref = useRef()

  const type = () => {
    switch (props.type) {
      case 'radio':
        return <RadioGroup {...props} />
      case 'searchSelect':
        return <Select placeholder={intl.formatMessage({ defaultMessage: 'Type to search...' })} {...props} searchable />
      case 'upload':
        return <Uploader upload={upload} {...props} />
      case 'currency':
        return <CurrencyInput {...props} />
      case 'bankDetails':
        return <BankDetails {...props} />
      case 'reasonForClaim':
        return <ClaimReasonInput {...props} />
      case 'date':
        return <DatePickerInput {...props} />
      case 'password':
        return <PasswordInput {...props} />
      case 'rewardTrackingOptIn':
        return <RewardTrackingOptIn label={label} {...props} />
      case 'purchaseInformation':
        return <PurchaseInformation formFields={formFields} upload={upload} {...props} />
      case 'purchaseItems':
        return <PurchaseItems formFields={formFields} fieldsToRender={fieldsToRender} upload={upload} {...props} />
      default:
        return props.type
    }
  }

  const renderHint = () => {
    return (
      <>
        {label}
        <span className={'field__hint'} ref={ref}>
          <Icon name={'info'} colour={'Light'} size={'Small'} />
        </span>
        <Tooltip className="field__tooltip" attachTo={ref.current} align={'Right'}>
          <span
            dangerouslySetInnerHTML={{
              __html: markdown.toHTML(tooltip.text).replace(/<a\shref/, '<a class="link" target="_blank" href'),
            }}
          />
        </Tooltip>
      </>
    )
  }

  const renderHintWrapper = () => {
    if (props.type === 'checkbox') {
      return <div>{renderHint()}</div>
    }

    return <span>{renderHint()}</span>
  }

  const valueIsValid = () => {
    if (props.type === 'purchaseInformation' || props.type === 'purchaseItems') {
      return valid
    }

    return props.value || props.value === false || props.value === 0
  }

  const getState = () => {
    if (props.value === null) return null
    if (!props.disabled && valueIsValid() && props.type !== 'bankDetails') {
      return valueIsValid() && valid ? 'Success' : 'Error'
    }

    return null
  }

  const getMsg = () => {
    if (props.type === 'currency' && props.value && valid === false) {
      return intl.formatMessage({ defaultMessage: 'Please enter a valid amount e.g. 1.50 for £1.50' })
    }

    if (props.type === 'currency' && props.value) {
      return props.value.displayAmount
    }

    return props.msg
  }

  const getClassName = () => {
    let className = ''

    if (props.className) className += props.className

    if (props.type) className += ` form-field-${props.type.toLowerCase()}`

    if (props.name) className += ` form-field-${props.name.toLowerCase()}`

    if (props.readOnly) className += ' form-field-readonly'

    return className.trim()
  }

  const elementType = type()

  const shouldRenderLabel = () => {
    if (props.type === 'rewardTrackingOptIn' && props.required) {
      return false
    }

    return true
  }

  return (
    <Fragment>
      <BaseField
        placeholder={elementType === 'select' ? intl.formatMessage({ defaultMessage: '-- Select --' }) : null }
        {...props}
        className={getClassName()}
        label={shouldRenderLabel() ? (tooltip ? renderHintWrapper() : label) : undefined}
        type={elementType}
        msg={getMsg()}
        state={getState()}
        fullWidth
      />
    </Fragment>
  )
}

export default Field
